import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ReadingProgress from "@/pages/article/components/ReadingProgress";
import ArticleBody from "@/pages/article/components/ArticleBody";
import NotFound from "@/pages/NotFound";
import { articles, getArticle } from "@/mocks/articles";
import { getChannel } from "@/mocks/channels";
import { formatDate, formatViews, toneSoft } from "@/utils/display";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = getArticle(slug);
  const [copied, setCopied] = useState(false);

  const toc = useMemo(
    () =>
      (article?.content ?? [])
        .map((block, index) => ({ block, index }))
        .filter((item) => item.block.type === "h2")
        .map((item) => ({ id: `heading-${item.index}`, text: item.block.text ?? "" })),
    [article],
  );

  const related = useMemo(() => {
    if (!article) return [];
    const sameChannel = articles.filter((a) => a.channel === article.channel && a.slug !== article.slug);
    const others = articles.filter((a) => a.channel !== article.channel && a.slug !== article.slug);
    return [...sameChannel, ...others].slice(0, 3);
  }, [article]);

  const channel = getChannel(article?.channel ?? "");

  useSeo({
    title: article ? `${article.title} | ${channel?.name ?? ""} - 深瞳档案` : "档案未找到 | 深瞳档案",
    description: article ? article.excerpt : "该档案不存在或已调整。",
    keywords: article ? article.tags.join(",") : "深瞳档案",
    path: `/article/${slug}`,
    type: "article",
    image: article?.cover,
  });

  if (!article) return <NotFound />;

  const tone = channel?.tone ?? "primary";
  const shareUrl = `https://www.shentongdangan.cn/article/${article.slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.publishedAt,
      dateModified: article.publishedAt,
      inLanguage: "zh-CN",
      author: { "@type": "Person", name: article.author },
      publisher: { "@type": "Organization", name: "深瞳档案" },
      mainEntityOfPage: shareUrl,
      keywords: article.tags.join(","),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.shentongdangan.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: channel?.name ?? "栏目",
          item: `https://www.shentongdangan.cn/channel/${article.channel}`,
        },
        { "@type": "ListItem", position: 3, name: article.title, item: shareUrl },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={schema} />
      <ReadingProgress />

      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "栏目总览", path: "/channels" },
              { label: channel?.name ?? "栏目", path: `/channel/${article.channel}` },
              { label: article.title },
            ]}
          />
          <div className="pb-10">
            <div className="flex flex-wrap items-center gap-2">
              {channel && (
                <Link
                  to={`/channel/${channel.slug}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-opacity hover:opacity-80 ${toneSoft[tone]}`}
                >
                  {channel.name}
                </Link>
              )}
              <span className="rounded-md bg-background-200 px-2.5 py-1 text-xs text-foreground-600">
                {article.readMinutes} 分钟阅读
              </span>
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600 md:text-lg">
              {article.subtitle}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" aria-hidden="true" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true" />
                {formatDate(article.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true" />
                {formatViews(article.views)} 次阅读
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container-page grid grid-cols-1 gap-10 py-10 lg:grid-cols-[1fr_260px]">
        <article className="min-w-0">
          <figure className="overflow-hidden rounded-lg">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              className="h-[260px] w-full object-cover md:h-[420px]"
            />
          </figure>

          <p className="mt-8 rounded-md border-l-2 border-primary-400 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {article.excerpt}
          </p>

          <ArticleBody content={article.content} />

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
            <ul className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <li key={tag}>
                  <span className="inline-block whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800">
                    #{tag}
                  </span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={handleCopy}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className={copied ? "ri-check-line text-primary-600" : "ri-link"} aria-hidden="true" />
              {copied ? "链接已复制" : "复制本文链接"}
            </button>
          </div>
        </article>

        <aside className="hidden lg:block">
          <div className="sticky top-32 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">本文目录</h2>
            {toc.length === 0 ? (
              <p className="mt-3 text-xs text-foreground-500">本篇为短档案，暂无小节标题。</p>
            ) : (
              <ol className="mt-3 space-y-2.5">
                {toc.map((item, index) => (
                  <li key={item.id} className="flex items-start gap-2">
                    <span className="mt-0.5 text-[11px] text-primary-500">0{index + 1}</span>
                    <a
                      href={`#${item.id}`}
                      className="text-xs leading-relaxed text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ol>
            )}
            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="text-xs leading-relaxed text-foreground-500">
                本篇整理自公开信息，仅作观察与解读，不构成任何事实认定。
              </p>
            </div>
          </div>
        </aside>
      </div>

      <section aria-labelledby="related-heading" className="border-t border-background-200 bg-background-100">
        <div className="container-page py-14">
          <div className="flex items-end justify-between gap-4">
            <h2 id="related-heading" className="font-heading text-2xl font-bold text-foreground-950">
              相关推荐
            </h2>
            <Link
              to="/channels"
              className="whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              更多档案
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}